import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useEffect, useState } from 'react';
import type { MetaAdSetWithInsights } from 'services/api/metaInsights.api';
import { useLazyGetMetaAdSetsWithInsightsQuery } from 'services/api/metaInsights.api';
import { META_BLUE, NAVY, RADIUS_ITEM, STATUS_NEUTRAL, SURFACE_CONTAINER_LOW, WHATSAPP_GREEN } from 'styles/tokens';

import type { CampaignRow, MetaCampaignRowSource } from '../types';
import {
  formatAdSetDestinationType,
  formatAdSetOptimizationGoal,
  formatCampaignObjective,
  isWhatsAppDestinationType,
} from '../utils/metaTypeLabels';
import {
  type DerivedKpiInputs,
  DerivedKpis,
  formatCurrency,
  LeadCounts,
  LeadStatsStrip,
  labelSx,
  MetaInsightsKpiGrid,
  parseSpend,
  SectionHeader,
  sectionTitleSx,
  ViewLeadsLink,
} from './metaDrillDownPrimitives';

interface Props {
  row: CampaignRow;
  since: string;
  until: string;
  businessUnitId?: number;
  selectedAdSetId: string | null;
  onSelectAdSet: (adSet: MetaAdSetWithInsights) => void;
}

const MetaCampaignSheet: React.FC<Props> = ({ row, since, until, businessUnitId, selectedAdSetId, onSelectAdSet }) => {
  const source = row.raw as MetaCampaignRowSource;
  const enrichment = source.enrichment;
  const insights = source.insights?.insights ?? null;
  const currency = source.enrichment?.currency ?? row.currency ?? null;
  const metaConnectionId = enrichment?.metaConnectionId ?? null;
  const campaignId = enrichment?.metaCampaignId ?? null;

  const [getAdSets] = useLazyGetMetaAdSetsWithInsightsQuery();
  const [adSets, setAdSets] = useState<MetaAdSetWithInsights[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!metaConnectionId || !campaignId) return;
    let cancelled = false;
    setLoading(true);
    getAdSets({
      metaConnectionId,
      campaignId,
      since,
      until,
      ...(businessUnitId ? { businessUnitId } : {}),
      fields: ['spend', 'impressions', 'reach', 'clicks', 'cpc'],
    })
      .unwrap()
      .then((result) => {
        if (!cancelled) setAdSets(result.adSets);
      })
      .catch(() => {
        if (!cancelled) setAdSets([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [metaConnectionId, campaignId, since, until, businessUnitId, getAdSets]);

  const objectiveLabel = formatCampaignObjective(row.objective);

  const derivedKpiInputs: DerivedKpiInputs = {
    spend: parseSpend(insights?.spend),
    clicks: parseSpend(insights?.clicks),
    contactados: row.leadsContacted,
    inCrm: row.leadsInCrm,
    currency,
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {objectiveLabel && (
        <Box>
          <Typography sx={{ ...labelSx, mb: 0.75 }}>Objetivo</Typography>
          <Chip
            label={objectiveLabel}
            size="small"
            sx={{
              bgcolor: alpha(META_BLUE, 0.1),
              color: META_BLUE,
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 24,
              borderRadius: RADIUS_ITEM,
            }}
          />
        </Box>
      )}

      <Box>
        <SectionHeader title="Rendimiento en Meta Ads" source="meta" />
        <MetaInsightsKpiGrid insights={insights} currency={currency} />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <SectionHeader title="Leads en nuestro CRM" source="crm" />
        <LeadCounts
          inputs={{
            contactados: row.leadsContacted,
            inCrm: row.leadsInCrm,
          }}
        />
        <DerivedKpis inputs={derivedKpiInputs} />
      </Box>

      <ViewLeadsLink
        field="metaCampaignExternalId"
        value={campaignId}
        label="Ver leads de esta campaña"
        businessUnitId={businessUnitId}
        since={since}
        until={until}
      />

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Typography sx={sectionTitleSx}>Conjuntos de anuncios</Typography>
          {loading && <CircularProgress size={14} sx={{ color: META_BLUE }} />}
        </Box>

        {!loading && (!adSets || adSets.length === 0) && (
          <Typography sx={{ fontFamily: 'Jost, sans-serif', color: STATUS_NEUTRAL, fontSize: '0.85rem' }}>
            No hay AdSets disponibles para esta campaña en el período consultado.
          </Typography>
        )}

        {adSets && adSets.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {adSets.map((adSet) => {
              const isSelected = selectedAdSetId === adSet.id;
              const adSetSpend = parseSpend(adSet.insights?.spend);
              const optGoalLabel = formatAdSetOptimizationGoal(adSet.optimizationGoal);
              const destinationLabel = formatAdSetDestinationType(adSet.destinationType);
              const destinationColor = isWhatsAppDestinationType(adSet.destinationType) ? WHATSAPP_GREEN : META_BLUE;
              return (
                <Box
                  key={adSet.id}
                  onClick={() => onSelectAdSet(adSet)}
                  sx={{
                    borderRadius: RADIUS_ITEM,
                    bgcolor: isSelected ? alpha(META_BLUE, 0.08) : SURFACE_CONTAINER_LOW,
                    p: 1.75,
                    cursor: 'pointer',
                    transition: 'background-color 140ms ease',
                    '&:hover': { bgcolor: alpha(META_BLUE, 0.08) },
                    outline: isSelected ? `1px solid ${alpha(META_BLUE, 0.35)}` : 'none',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontFamily: 'Jost, sans-serif',
                          color: NAVY,
                          fontWeight: 600,
                          fontSize: '0.88rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {adSet.name}
                      </Typography>
                      {(optGoalLabel || destinationLabel) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25, flexWrap: 'wrap' }}>
                          {optGoalLabel && (
                            <Typography
                              sx={{
                                fontFamily: 'Jost, sans-serif',
                                color: alpha(NAVY, 0.5),
                                fontWeight: 500,
                                fontSize: '0.66rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                              }}
                            >
                              {optGoalLabel}
                            </Typography>
                          )}
                          {destinationLabel && (
                            <Chip
                              label={destinationLabel}
                              size="small"
                              sx={{
                                bgcolor: alpha(destinationColor, 0.1),
                                color: destinationColor,
                                fontFamily: 'Jost, sans-serif',
                                fontWeight: 600,
                                fontSize: '0.66rem',
                                height: 18,
                                borderRadius: RADIUS_ITEM,
                                '& .MuiChip-label': { px: 0.75 },
                              }}
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                    <Typography sx={{ fontFamily: 'Jost, sans-serif', color: alpha(NAVY, 0.6), fontSize: '0.78rem' }}>
                      {formatCurrency(adSet.insights?.spend ?? null, currency)}
                    </Typography>
                    <ChevronRightIcon sx={{ color: alpha(NAVY, 0.4), fontSize: '1.1rem' }} />
                  </Box>
                  <Box sx={{ pt: 1 }}>
                    <LeadStatsStrip stats={adSet.leadsStats} spend={adSetSpend} currency={currency} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MetaCampaignSheet;
